import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSession } from "./session";
import { Icon } from "../components/ui/Icon";
import { Button } from "../components/ui/primitives";

/** Everything behind sign-in waits here until the session is known. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useSession();
  const location = useLocation();

  if (status === "loading") return <FullPageWait />;

  if (status === "signedOut") {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}

/**
 * A route the user must hold a permission to open.
 *
 * This is not the security boundary — the API checks the same key on every call
 * — but without it a user who typed a URL would watch a screen load and then
 * fail request by request, which reads as a broken product rather than a
 * deliberate limit.
 */
export function RequirePermission({
  anyOf,
  allowExternal = false,
  children,
}: {
  anyOf: string[];
  /** Only for the few internal screens a portal user is meant to use, such as external abstract review. */
  allowExternal?: boolean;
  children: ReactNode;
}) {
  const { canAny, user } = useSession();

  // Internal screens are not part of the portal. A portal user who follows an
  // internal link goes back to the portal instead of seeing an internal shell
  // whose requests are then refused one by one.
  if (user?.isExternal && !allowExternal) return <Navigate to="/portal" replace />;

  if (!canAny(...anyOf)) return <NotPermitted />;

  return <>{children}</>;
}

/** Portal users are kept out of internal screens, and vice versa. */
export function RequireInternal({ children }: { children: ReactNode }) {
  const { user } = useSession();

  if (user?.isExternal) return <Navigate to="/portal" replace />;

  return <>{children}</>;
}

export function FullPageWait() {
  return (
    <div
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        background: "var(--ink-900)",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <img
          src="/brand/wolfray-ai-logo.png"
          alt=""
          style={{ width: 80, opacity: 0.85, marginBottom: 14 }}
        />
        <div className="muted" style={{ font: "var(--t-small)" }}>
          Loading your workspace…
        </div>
      </div>
    </div>
  );
}

export function NotPermitted() {
  return (
    <div className="state" style={{ minHeight: 420 }}>
      <Icon.lock />
      <b>You do not have access to this</b>
      <p>
        Your account does not hold the permission this screen needs. If you think
        that is wrong, ask your administrator to review your group membership.
      </p>
      <Button onClick={() => window.history.back()}>Go back</Button>
    </div>
  );
}

export function NotFound() {
  return (
    <div className="state" style={{ minHeight: 420 }}>
      <Icon.search />
      <b>That page does not exist</b>
      <p>The address you followed does not match anything in EventOS.</p>
      <Button onClick={() => window.history.back()}>Go back</Button>
    </div>
  );
}

/** Screens that need an event selected say so rather than failing to load. */
export function NoEventSelected() {
  return (
    <div className="state" style={{ minHeight: 320 }}>
      <Icon.calendar />
      <b>Choose an event first</b>
      <p>
        This screen works inside one event. Pick one from the selector at the top
        of the page.
      </p>
    </div>
  );
}
